import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
  Chip,
  TextField
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Edit as EditIcon } from '@mui/icons-material';
import { workOrdersApi } from '../../../services/api';

const EditWorkOrderDialog = ({ 
  open, 
  onClose, 
  workOrder,
  onUpdate 
}) => {
  const [formData, setFormData] = useState({
    priority: '',
    due_date: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && workOrder) {
      setFormData({
        priority: workOrder.priority || 'normal',
        due_date: workOrder.due_date ? new Date(workOrder.due_date) : null
      });
      setError(null);
    }
  }, [open, workOrder]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const updateData = {
        priority: formData.priority,
        due_date: formData.due_date ? formData.due_date.toISOString() : null
      };

      await workOrdersApi.updateWorkOrder(workOrder.id, updateData);

      if (onUpdate) {
        await onUpdate();
      }

      onClose();
    } catch (err) {
      console.error('Error updating work order:', err);
      setError(err.response?.data?.error || 'Failed to update work order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon />
        Edit Action Log
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Work Order Details
            </Typography>
            <Typography variant="body2">
              <strong>Number:</strong> {workOrder?.work_order_number}
            </Typography>
            <Typography variant="body2">
              <strong>Title:</strong> {workOrder?.title}
            </Typography>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              label="Priority"
              onChange={(e) => handleInputChange('priority', e.target.value)}
              disabled={loading}
            >
              <MenuItem value="low">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip label="Low" size="small" color="success" />
                  <Typography>Low</Typography>
                </Stack>
              </MenuItem>
              <MenuItem value="normal">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip label="Normal" size="small" color="info" />
                  <Typography>Normal</Typography>
                </Stack>
              </MenuItem>
              <MenuItem value="high">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip label="High" size="small" color="warning" />
                  <Typography>High</Typography>
                </Stack>
              </MenuItem>
              <MenuItem value="critical">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip label="Critical" size="small" color="error" />
                  <Typography>Critical</Typography>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>

          <DateTimePicker
            label="Due Date & Time"
            value={formData.due_date}
            onChange={(newValue) => handleInputChange('due_date', newValue)}
            renderInput={(params) => <TextField {...params} fullWidth />}
            format="dd/MM/yyyy hh:mm a"
            disabled={loading}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          color="primary"
          sx={{ px: 3 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Update Action Log'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditWorkOrderDialog;